import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Gift, ArrowRight, ChevronRight, Cake, Circle, User, Diamond, Heart, Flame, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import './GiftingPage.css';

const GiftingPage = () => {
  const navigate = useNavigate();
  const { lang, categories } = useContext(AppContext);
  const isMr = lang === 'mr';
  const [curatorCategory, setCuratorCategory] = useState('');
  const [curatorPrice, setCuratorPrice] = useState('');
  const [curatorRelation, setCuratorRelation] = useState('');

  const RECIPIENT_CARDS = [
    {
      slug: 'for-her',
      id: 'For Her',
      title: isMr ? 'तिच्यासाठी भेटवस्तू' : 'Gifts For Her',
      subtitle: isMr ? 'रिंग्स · ब्रेसलेट्स · चेन्स · मेखला' : 'Rings · Bracelets · Chains · Mekhala',
      images: ['images/w br11.jpg', 'images/w ring1.jpg', 'images/w chain13.jpg'],
      count: isMr ? '१८+ आयटम्स' : '18+ Items'
    },
    {
      slug: 'for-him',
      id: 'For Him',
      title: isMr ? 'त्याच्यासाठी भेटवस्तू' : 'Gifts For Him',
      subtitle: isMr ? 'क्युबन ब्रेसलेट्स · चेन्स · ओनिक्स रिंग्स' : 'Cuban Bracelets · Chains · Onyx Rings',
      images: ['images/men br6.jpg', 'images/men ring10.jpg', 'images/men chain10.jpg'],
      count: isMr ? '१२+ आयटम्स' : '12+ Items'
    },
    {
      slug: 'for-baby',
      id: 'For Baby',
      title: isMr ? 'बाळांसाठी आणि मुलांसाठी' : 'For Baby & Kids',
      subtitle: isMr ? 'शुद्ध चांदीचे पैंजण · मऊ चार्म्स' : 'Pure Silver Painjan · Soft Charms',
      images: ['images/painjan5.jpg', 'images/painjan7.jpg', 'images/painjan2.jpg'],
      count: isMr ? '८+ आयटम्स' : '8+ Items'
    }
  ];

  const OCCASION_CARDS = [
    { slug: 'birthday', title: isMr ? 'वाढदिवस' : 'Birthday', subtitle: isMr ? 'कायमस्वरूपी आठवणी' : 'Timeless Keepsakes', image: 'images/w ring1.jpg', icon: <Cake size={24} /> },
    { slug: 'anniversary', title: isMr ? 'लग्नाचा वाढदिवस' : 'Anniversary', subtitle: isMr ? 'सॉलिटेअर आणि गोल्ड सेट्स' : 'Solitaires & Gold Sets', image: 'images/w chain13.jpg', icon: <Circle size={24} /> },
    { slug: 'wedding', title: isMr ? 'लग्न आणि वधूसाठी' : 'Wedding & Bridal', subtitle: isMr ? 'रॉयल कुंदन आणि मेखला' : 'Royal Kundan & Mekhala', image: 'images/waist12.jpg', icon: <User size={24} /> },
    { slug: 'engagement', title: isMr ? 'साखरपुडा' : 'Engagement', subtitle: isMr ? 'कायमस्वरूपी हिरे' : 'Forever Diamonds', image: 'images/w ring3.jpg', icon: <Diamond size={24} /> },
    { slug: 'valentines-day', title: isMr ? 'व्हॅलेंटाईन्स डे' : 'Valentine\'s Day', subtitle: isMr ? 'हार्ट डायमंड रिंग्ज' : 'Heart Diamond Rings', image: 'images/w ring14.jpg', icon: <Heart size={24} /> },
    { slug: 'baby-naming-ceremony', title: isMr ? 'बाळाचा नामकरण सोहळा' : 'Baby\'s naming ceremony', subtitle: isMr ? 'दैवी आशीर्वाद' : 'Divine Blessings', image: 'images/painjan10.jpg', icon: <Flame size={24} /> }
  ];

  const handleCuratorSubmit = () => {
    if (!curatorRelation && !curatorCategory) return;
    const slugMap = { 'For Her': 'for-her', 'For Him': 'for-him', 'For Baby': 'for-baby' };
    const occSlugMap = {
      'Birthday': 'birthday',
      'Anniversary': 'anniversary',
      'Wedding': 'wedding',
      'Engagement': 'engagement',
      'Valentine\'s Day': 'valentines-day',
      'Silver Idols': 'silver-idols'
    };
    if (slugMap[curatorRelation]) navigate(`/gifting/${slugMap[curatorRelation]}`);
    else if (occSlugMap[curatorCategory]) navigate(`/gifting/${occSlugMap[curatorCategory]}`);
    else navigate('/gifting/for-her');
  };

  return (
    <div className="gifting-page">
      <section className="gp-concept-banner">
        <div className="gp-concept-bg">
          <img src="images/banner.png" alt="Gifting Collection Banner" />
          <div className="gp-concept-overlay"></div>
        </div>
        <div className="gp-concept-content">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            {isMr ? 'कायमची आठवण राहील अशी भेट द्या' : 'Give the Gift of Eternity'}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            {isMr ? 'विशेष निवडलेले सोन्या-चांदीचे दागिने — प्रत्येक प्रसंग बनवतात जन्मभराची आठवण' : 'Curated gold & silver jewellery — turning every occasion into a lifetime memory'}
          </motion.p>
        </div>
      </section>

      <section className="gp-recipient-cards-section">
        <div className="gp-section-title">
          <span className="gold-sub-label">{isMr ? 'नात्यानुसार भेटवस्तू' : 'GIFTING BY RELATION'}</span>
          <h2>{isMr ? 'तुम्ही कोणासाठी खरेदी करत आहात?' : 'Who Are You Shopping For?'}</h2>
          <p>{isMr ? 'त्यांच्यासाठी खास तयार केलेले भेटवस्तूंचे संग्रह पाहण्यासाठी निवड करा' : 'Select a recipient to explore curated gift collections crafted just for them'}</p>
        </div>
        <div className="recipient-cards-grid">
          {RECIPIENT_CARDS.map((rc, i) => (
            <motion.div
              key={rc.id}
              className="recipient-card clean-recipient-card"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12 }}
              whileHover={{ y: -8 }}
              onClick={() => navigate(`/gifting/${rc.slug}`)}
            >
              <div className="rc-collage-wrap">
                <img src={rc.images[0]} alt={rc.title} className="rc-c-img main" />
                <img src={rc.images[1]} alt={rc.title} className="rc-c-img sub1" />
                <img src={rc.images[2]} alt={rc.title} className="rc-c-img sub2" />
                <div className="rc-collage-overlay"></div>
              </div>
              <div className="rc-overlay">
                <span className="rc-badge">{rc.count}</span>
                <h3>{rc.title}</h3>
                <p>{rc.subtitle}</p>
                <button className="btn-rc-explore">{isMr ? 'भेटवस्तू पहा' : 'View Gifts'} <ArrowRight size={14} /></button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="gift-curator-section">
        <div className="curator-layout">
          <div className="curator-form-area">
            <span className="curator-eyebrow">✦ {isMr ? 'जीडीआर गिफ्ट क्युरेटर' : 'GDR GIFT CURATOR'}</span>
            <h2>{isMr ? 'काय घ्यावे याची खात्री नाही?' : 'Not Sure What To Buy?'}<br /><strong>{isMr ? 'आमचा गिफ्ट क्युरेटर वापरून पहा!' : 'Try Our Gift Curator!'}</strong></h2>
            <p className="curator-sub">{isMr ? '३ साध्या प्रश्नांची उत्तरे द्या आणि आम्ही तुमच्यासाठी योग्य दागिन्यांची भेट शोधू.' : 'Answer 3 simple questions and we\'ll find the perfect jewellery gift for you.'}</p>

            <div className="curator-form">
              <div className="curator-sentence">
                <span>{isMr ? 'मला उत्कृष्ट' : 'I am looking for exquisite'}</span>
                <select value={curatorCategory} onChange={e => setCuratorCategory(e.target.value)}>
                  <option value="">{isMr ? 'श्रेणी निवडा' : 'Choose Category'}</option>
                  {Array.from(new Set((categories || []).map(c => c.name))).filter(Boolean).map(cName => (
                    <option key={cName} value={cName}>{cName}</option>
                  ))}
                </select>
                <span>{isMr ? 'दागिने हवे आहेत,' : 'jewellery,'}</span>
              </div>

              <div className="curator-sentence">
                <span>{isMr ? 'बजेट' : 'within'}</span>
                <select value={curatorPrice} onChange={e => setCuratorPrice(e.target.value)}>
                  <option value="">{isMr ? 'किंमत श्रेणी निवडा' : 'Choose Price Range'}</option>
                  <option value="under5k">{isMr ? '₹५,००० च्या खाली' : 'Under ₹5,000'}</option>
                  <option value="5k-20k">{isMr ? '₹५,००० - ₹२०,०००' : '₹5,000 – ₹20,000'}</option>
                  <option value="over20k">{isMr ? '₹२०,००० च्या वर' : 'Over ₹20,000'}</option>
                </select>
                <span>{isMr ? 'मध्ये,' : 'for'}</span>
                <select value={curatorRelation} onChange={e => setCuratorRelation(e.target.value)}>
                  <option value="">{isMr ? 'नाती निवडा' : 'Choose Relation'}</option>
                  <option>For Her</option>
                  <option>For Him</option>
                  <option>For Baby</option>
                </select>
              </div>

              <button className="btn-curator-continue" onClick={handleCuratorSubmit}>
                {isMr ? 'पुढे जा' : 'Continue'} <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="curator-img-area">
            <div className="dual-gifting-boxes-container">
              <div className="gifting-box-3d red-velvet">
                <div className="box-ribbon-cross"></div>
                <div className="box-cap"></div>
                <div className="box-body-inner">
                  <span className="box-inner-glow"><Sparkles size={20} /></span>
                  <div className="box-content-tag">{isMr ? 'स्पेशल चांदी' : 'Silver Special'}</div>
                </div>
              </div>

              <div className="gifting-box-3d gold-royal">
                <div className="box-ribbon-cross"></div>
                <div className="box-cap"></div>
                <div className="box-body-inner">
                  <span className="box-inner-glow"><Star size={20} /></span>
                  <div className="box-content-tag">{isMr ? 'सिग्नेचर सोने' : 'Gold Signature'}</div>
                </div>
              </div>
            </div>
            <div className="curator-gift-badge" style={{ marginTop: '1.5rem', position: 'relative', width: '100%', boxSizing: 'border-box' }}>
              <Gift size={20} />
              <span>{isMr ? 'सिग्नेचर पॅकेजिंग मोफत समाविष्ट' : 'Signature Packaging Included Free'}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="gp-occasions-section">
        <div className="gp-section-title">
          <span className="gold-sub-label">{isMr ? 'खास क्षण' : 'SPECIAL MOMENTS'}</span>
          <h2>{isMr ? 'प्रसंगानुसार भेटवस्तू' : 'Gift by Occasion'}</h2>
          <p>{isMr ? 'आयुष्यातील सर्वात अनमोल क्षणांसाठी निवडलेले संग्रह' : 'Handpicked collections for life\'s most precious milestones'}</p>
        </div>
        <div className="occasions-cards-grid-3col">
          {OCCASION_CARDS.map((occ, i) => (
            <motion.div
              key={occ.slug}
              className="occ-card clean-occ-card"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ scale: 1.03, y: -5 }}
              onClick={() => navigate(`/gifting/${occ.slug}`)}
            >
              <div className="occ-card-img-wrap">
                <img src={occ.image} alt={occ.title} />
                <div className="occ-card-overlay"></div>
                <div className="occ-emoji">{occ.icon}</div>
              </div>
              <div className="occ-card-body">
                <h4>{occ.title}</h4>
                <p>{occ.subtitle}</p>
                <span className="occ-arrow">{isMr ? 'पहा →' : 'Explore →'}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default GiftingPage;
